import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Assets } from '../../../api/client/Assets';
import { AssetStates } from '../../../api/client/AssetStates';
import {
  AssetResponse,
  CreateAssetDto,
  CreateStateDto,
  CreateTagDto,
  ReadAssetDto,
  StateResponse,
  TagResponse,
  UpdateAssetCostValueDto,
  UpdateAssetDto,
  UpdateStateDto,
  UpdateTagDto,
} from '../../../api/client/data-contracts';
import { Tags } from '../../../api/client/Tags';
import { IntlShape, useIntl } from 'react-intl';
import { notification } from 'antd';
import { RiCheckboxCircleFill, RiErrorWarningFill } from 'react-icons/ri';
import { defaultApiConfig } from '../../../api';
import { useHistory } from 'react-router-dom';

export interface QueriedData<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
}

export interface UseAssetsResult {
  assetsQuery: QueriedData<AssetResponse[]>;
  createAsset: (createAssetDto: CreateAssetDto) => void;
  createCustomerAsset: (data: {
    customerId: string;
    createAssetDto: CreateAssetDto;
  }) => void;
  updateAsset: (data: { serialNumber: string; update: UpdateAssetDto }) => void;
  updateAssetCostValue: (data: {
    serialNumber: string;
    update: Omit<UpdateAssetCostValueDto, 'id'>;
  }) => void;
  deleteAsset: (serialNumber: string) => void;
}

export const successIcon = (
  <RiCheckboxCircleFill style={{ color: '#00F7BF' }} />
);
export const errorIcon = <RiErrorWarningFill style={{ color: '#FF0022' }} />;

export function showSuccessNotification(
  descriptionMessageId: string,
  intl?: IntlShape,
) {
  const message = intl ? intl.formatMessage({ id: 'success' }) : 'Sucesso';
  const description = intl
    ? intl.formatMessage({ id: descriptionMessageId })
    : descriptionMessageId;
  notification.open({
    message,
    description,
    icon: successIcon,
    placement: 'bottomRight',
  });
}
export function showErrorNotification(
  descriptionMessageId: string,
  intl?: IntlShape,
) {
  const message = intl ? intl.formatMessage({ id: 'error' }) : 'Erro';
  const description = intl
    ? intl.formatMessage({ id: descriptionMessageId })
    : descriptionMessageId;

  notification.open({
    message,
    description,
    icon: errorIcon,
    placement: 'bottomRight',
  });
}

const assetsApi = new Assets(defaultApiConfig);

export function useAssets(query?: ReadAssetDto): UseAssetsResult {
  const queryKey = ['assets', JSON.stringify(query)];

  const baseQueryKey = queryKey[0];
  let queryParams = query;

  if (!query || Object.entries(query).length === 0) {
    queryParams = undefined;
  }

  const assetsQuery = useQuery(queryKey, () =>
    assetsApi
      .findAll(queryParams || {})
      // Only get the equipments not associated to any customer when customerId is not passed
      .then((res) =>
        queryParams?.customerId
          ? res.data
          : res.data.filter((a) => a.customerId === null),
      ),
  );

  const queryClient = useQueryClient();
  const intl = useIntl();

  const { mutate: createAsset } = useMutation(
    (createAssetDto: CreateAssetDto) =>
      assetsApi.create(createAssetDto).then((res) => res.data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(baseQueryKey);
        const successMessage = intl.formatMessage({ id: 'success' });
        const successDescription = intl.formatMessage({
          id: 'asset-create-success-message',
        });
        notification.open({
          message: successMessage,
          description: successDescription,

          placement: 'bottomRight',
          icon: successIcon,
        });
      },
      onError: (data) => {
        const errorMessage = intl.formatMessage({ id: 'error' });
        const errorMessageDescription = intl.formatMessage({
          id: 'asset-create-fail-message',
        });

        notification.open({
          message: errorMessage,
          description: errorMessageDescription,

          placement: 'bottomRight',
          icon: errorIcon,
        });
      },
    },
  );
  const { mutate: createCustomerAsset } = useMutation(
    (data: { customerId: string; createAssetDto: CreateAssetDto }) =>
      assetsApi
        .createCustomerAsset(data.customerId, data.createAssetDto)
        .then((res) => res.data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(baseQueryKey);
        const successMessage = intl.formatMessage({ id: 'success' });
        const successDescription = intl.formatMessage({
          id: 'asset-create-success-message',
        });
        notification.open({
          message: successMessage,
          description: successDescription,

          placement: 'bottomRight',
          icon: successIcon,
        });
      },
      onError: (data) => {
        const errorMessage = intl.formatMessage({ id: 'error' });
        const errorMessageDescription = intl.formatMessage({
          id: 'asset-create-fail-message',
        });

        notification.open({
          message: errorMessage,
          description: errorMessageDescription,

          placement: 'bottomRight',
          icon: errorIcon,
        });
      },
    },
  );

  const { mutate: updateAsset } = useMutation(
    (data: { serialNumber: string; update: UpdateAssetDto }) =>
      assetsApi
        .update({ ...data.update, id: data.serialNumber })
        .then((res) => res.data),
    {
      onSuccess: (data) => {
        const successMessage = intl.formatMessage({ id: 'success' });
        const successDescription = intl.formatMessage({
          id: 'asset-update-success-message',
        });

        notification.open({
          message: successMessage,
          description: successDescription,

          placement: 'bottomRight',
          icon: successIcon,
        });
        queryClient.invalidateQueries(baseQueryKey);
      },
      onError: (data) => {
        const errorMessage = intl.formatMessage({ id: 'error' });
        const errorMessageDescription = intl.formatMessage({
          id: 'asset-create-fail-message',
        });

        notification.open({
          message: errorMessage,
          description: errorMessageDescription,

          placement: 'bottomRight',
          icon: errorIcon,
        });
      },
    },
  );
  const { mutate: updateAssetCostValue } = useMutation(
    (data: { serialNumber: string; update: Omit<UpdateAssetCostValueDto, 'id'> }) =>
      assetsApi
        .updateCostValue({
          ...data.update,
          id: data.serialNumber,
        })
        .then((res) => res.data),
    {
      onSuccess: (data) => {
        notification.open({
          message: 'Sucesso',
          description: 'Custo do equipamento atualizado com sucesso',

          placement: 'bottomRight',
          icon: successIcon,
        });
        queryClient.invalidateQueries(baseQueryKey);
      },
      onError: (data) => {
        notification.open({
          message: 'Erro',
          description: 'Não foi possível atualizar o custo do equipamento',

          placement: 'bottomRight',
          icon: errorIcon,
        });
      },
    },
  );

  const history = useHistory();
  const { mutate: deleteAsset } = useMutation(
    (serialNumber: string) =>
      assetsApi.remove({ id: serialNumber }).then((res) => res.data),
    {
      onSuccess: (data) => {
        history.push('/apps/cockpit/inventory');
        notification.open({
          message: 'Sucesso',
          description: 'Equipamento eliminado com sucesso',

          placement: 'bottomRight',
          icon: successIcon,
        });
        queryClient.invalidateQueries(baseQueryKey);
      },
      onError: (data) => {
        notification.open({
          message: 'Erro',
          description:
            'Não foi possível eliminar o equipamento. O equipamento pode ter manutenções ou alugers associados',

          placement: 'bottomRight',
          icon: errorIcon,
        });
      },
    },
  );

  return {
    assetsQuery,
    createAsset,
    createCustomerAsset,
    updateAsset,
    updateAssetCostValue,
    deleteAsset,
  };
}

export interface UseAssetStateResult {
  statesQuery: QueriedData<StateResponse[]>;
  createState: (data: CreateStateDto) => void;
  updateState: (data: { id: string; update: UpdateStateDto }) => void;

  getStateById(id: string): StateResponse | null;
}

const assetStatesApi = new AssetStates(defaultApiConfig);

export function useAssetStates(): UseAssetStateResult {
  const queryKey = 'assets-states';
  const statesQuery = useQuery(queryKey, () =>
    assetStatesApi.findAll().then((res) => res.data),
  );
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { mutate: createState } = useMutation(
    (data: CreateStateDto) =>
      assetStatesApi.create(data).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('asset-state-create-success-message', intl);
        queryClient.invalidateQueries(queryKey);
      },

      onError: (data) => {
        showErrorNotification('asset-state-create-fail-message', intl);
      },
    },
  );

  const { mutate: updateState } = useMutation(
    (data: { id: string; update: UpdateStateDto }) =>
      assetStatesApi.update(data.id, data.update).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('asset-state-update-success-message', intl);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (data) => {
        showErrorNotification('asset-state-update-fail-message', intl);
      },
    },
  );

  function getStateById(id: string) {
    return statesQuery.data?.find((state) => state.id === id) || null;
  }

  return { getStateById, statesQuery, createState, updateState };
}

const tagsApi = new Tags(defaultApiConfig);

export interface UseTagsResult {
  tagsQuery: QueriedData<TagResponse[]>;
  createTag: (tag: CreateTagDto) => void;
  updateTag: (data: { id: string; update: UpdateTagDto }) => void;
  getTagsByIds(ids: string[]): TagResponse[] | null;
}
export function useTags(): UseTagsResult {
  const queryKey = ['tags'];
  const tagsQuery = useQuery(queryKey, () =>
    tagsApi.findAll().then((res) => res.data),
  );

  const queryClient = useQueryClient();
  const intl = useIntl();
  const { mutate: createTag } = useMutation(
    (data: CreateTagDto) => tagsApi.create(data).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('tag-create-success-message', intl);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (data) => {
        showErrorNotification('tag-create-fail-message', intl);
      },
    },
  );

  const { mutate: updateTag } = useMutation(
    (data: { id: string; update: UpdateTagDto }) =>
      tagsApi.updateDescription(data.id, data.update).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('tag-update-success-message', intl);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (data) => {
        showErrorNotification('tag-update-fail-message', intl);
      },
    },
  );

  function getTagsByIds(ids: string[]) {
    if (!tagsQuery.data) {
      return null;
    }

    return tagsQuery.data.filter((tag) => ids.includes(tag.id));
  }

  return { getTagsByIds, tagsQuery, createTag, updateTag };
}
